'use client';

import { register, signIn } from '@eliadmin/core/src/api/customers/customers-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { fetchCountries } from '@eliadmin/core/src/api/general/general-api';
import type { Country } from '@eliadmin/core/src/api/general/types/general-types';
import { PhoneInputField } from '@eliadmin/core/src/components/ui/phone-input/phone-input';
import { Toaster } from '@eliadmin/core/src/components/ui/toaster';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { setAuthToken } from '@eliadmin/core/src/server/api-client';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import {
  ArrowLeft,
  Building2,
  Check,
  ChevronDown,
  Eye,
  EyeOff,
  Lock,
  Mail,
  Phone,
  SlidersHorizontal,
  User,
  type LucideIcon,
} from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';

import { LisonexConfiguratorStyles } from './configurator-page';

type AuthMode = 'login' | 'register';
type RegisterStep = 1 | 2 | 3;
type CustomerKind = 'person' | 'company';

type AuthState = {
  email: string;
  password: string;
  passwordConfirmation: string;
  name: string;
  surname: string;
  company: string;
  ico: string;
  dic: string;
  icDph: string;
  street: string;
  streetNumber: string;
  postcode: string;
  city: string;
  country: string;
  contactName: string;
  contactEmail: string;
  phone: string;
  gdpr: boolean;
  customerKind: CustomerKind;
};

type PendingAuth = {
  token: string;
  customer: any;
};

const initialState: AuthState = {
  email: '',
  password: '',
  passwordConfirmation: '',
  name: '',
  surname: '',
  company: '',
  ico: '',
  dic: '',
  icDph: '',
  street: '',
  streetNumber: '',
  postcode: '',
  city: '',
  country: '',
  contactName: '',
  contactEmail: '',
  phone: '',
  gdpr: false,
  customerKind: 'person',
};

const AUTH_STRING_KEYS = [
  'email',
  'password',
  'passwordConfirmation',
  'name',
  'surname',
  'company',
  'ico',
  'dic',
  'icDph',
  'street',
  'streetNumber',
  'postcode',
  'city',
  'country',
  'contactName',
  'contactEmail',
  'phone',
] as const satisfies readonly (keyof AuthState)[];

const authBackgroundUrl = '/images/configurator/lisonex-auth-bg.png';
const TRUNK_PREFIX_DIAL_CODES = ['421', '420'];

function normalizeSlovakPhone(phone: string) {
  const trimmed = phone.trim();
  if (!trimmed) return '';

  const digits = trimmed.replace(/\D/g, '');
  const matchingDialCode = TRUNK_PREFIX_DIAL_CODES.find(dialCode => digits.startsWith(dialCode));

  if (matchingDialCode) {
    const nationalNumber = digits.slice(matchingDialCode.length);
    return `+${matchingDialCode}${nationalNumber.startsWith('0') ? nationalNumber.slice(1) : nationalNumber}`;
  }

  if (digits.startsWith('421')) return `+${digits}`;
  if (digits.startsWith('0')) return `+421${digits.slice(1)}`;

  return `+421${digits}`;
}

function resolveDefaultCountryId(countries: Country[]) {
  return (
    countries.find(country => country.id.toLowerCase() === 'sk')?.id ??
    countries[0]?.id ??
    ''
  );
}

function LisonexWordmark({ className }: { className?: string }) {
  return (
    <div
      className={cn('font-bold uppercase leading-none tracking-[-0.06em] text-black', className)}
    >
      LISONEX
    </div>
  );
}

function IconBox({ icon: Icon, active }: { icon: LucideIcon; active?: boolean }) {
  return (
    <span
      className={cn(
        'flex h-9 w-9 items-center justify-center',
        active ? 'text-white' : 'text-[#777]',
      )}
    >
      <Icon className="h-5 w-5" strokeWidth={1.8} />
    </span>
  );
}

function TextInput({
  icon: Icon,
  placeholder,
  type = 'text',
  value,
  onChange,
  autoComplete,
  rightIcon,
}: {
  icon?: LucideIcon;
  placeholder: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete?: string;
  rightIcon?: LucideIcon;
}) {
  const RightIcon = rightIcon;
  const isPasswordField = type === 'password';
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const inputType = isPasswordField && isPasswordVisible ? 'text' : type;
  const PasswordIcon = isPasswordVisible ? EyeOff : Eye;

  return (
    <label className="block">
      <span className="sr-only">{placeholder}</span>
      <div className="auth-field flex h-[74px] items-center border border-[#dedede] bg-white transition focus-within:border-[#e87500]">
        {Icon && (
          <span className="flex w-14 justify-center text-[#777]">
            <Icon className="h-5 w-5" strokeWidth={1.7} />
          </span>
        )}
        <input
          type={inputType}
          value={value ?? ''}
          autoComplete={autoComplete}
          placeholder={placeholder}
          onChange={event => onChange(event.target.value)}
          className={cn(
            'h-full min-w-0 flex-1 bg-transparent text-[20px] font-medium text-black outline-none placeholder:text-[#777]',
            !Icon && 'px-6',
          )}
        />
        {isPasswordField ? (
          <button
            type="button"
            aria-label={isPasswordVisible ? 'Skryť heslo' : 'Zobraziť heslo'}
            aria-pressed={isPasswordVisible}
            onClick={() => setIsPasswordVisible(previous => !previous)}
            className="flex h-full w-14 shrink-0 items-center justify-center text-[#777] transition hover:text-[#e87500] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-4px] focus-visible:outline-[#e87500]"
          >
            <PasswordIcon className="h-5 w-5" strokeWidth={1.7} />
          </button>
        ) : RightIcon ? (
          <span className="flex w-14 justify-center text-[#777]">
            <RightIcon className="h-5 w-5" strokeWidth={1.7} />
          </span>
        ) : null}
      </div>
    </label>
  );
}

function AuthCheckbox({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 text-[18px] font-semibold leading-snug text-black">
      <span
        className={cn(
          'relative mt-1 flex h-6 w-6 shrink-0 items-center justify-center border transition',
          checked ? 'border-[#e87500] bg-[#e87500]' : 'border-[#dedede] bg-white',
        )}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={event => onChange(event.target.checked)}
          className="absolute inset-0 cursor-pointer opacity-0"
        />
        {checked && <Check className="h-4 w-4 text-white" strokeWidth={2.7} />}
      </span>
      <span>{children}</span>
    </label>
  );
}

function ModeTabs({ mode, onChange }: { mode: AuthMode; onChange: (mode: AuthMode) => void }) {
  return (
    <div className="auth-tabs grid h-[52px] w-full grid-cols-2 bg-[#f1f1f1] p-1">
      {(['login', 'register'] as AuthMode[]).map(item => {
        const isActive = mode === item;

        return (
          <button
            key={item}
            type="button"
            onClick={() => onChange(item)}
            className={cn(
              'text-[16px] font-semibold transition',
              isActive
                ? 'border border-[#e4e4e4] bg-white text-black shadow-[0_0_0_2px_rgba(0,0,0,0.02)]'
                : 'text-[#707070] hover:bg-white/45 hover:text-black',
            )}
          >
            {item === 'login' ? 'Prihlásenie' : 'Registrácia'}
          </button>
        );
      })}
    </div>
  );
}

function PrimaryButton({
  children,
  type = 'button',
  disabled,
  onClick,
  className,
}: {
  children: ReactNode;
  type?: 'button' | 'submit';
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'inline-flex h-[54px] items-center justify-center gap-3 bg-[#e87500] px-7 text-[16px] font-bold uppercase text-white transition hover:bg-[#cf6900] disabled:cursor-not-allowed disabled:opacity-45',
        className,
      )}
    >
      {children}
    </button>
  );
}

function SecondaryButton({
  children,
  onClick,
  className,
}: {
  children: ReactNode;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex h-[54px] items-center justify-center gap-3 bg-[#fbefe6] px-7 text-[16px] font-bold uppercase text-[#d86600] transition hover:bg-[#f7dfcc]',
        className,
      )}
    >
      {children}
    </button>
  );
}

function AuthShell({ children }: { children: ReactNode }) {
  return (
    <main
      data-configurator-main
      data-lisonex-auth
      className="lisonex-configurator h-screen overflow-hidden bg-white text-black"
    >
      <LisonexConfiguratorStyles />
      <style jsx global>{`
        body.ea-configurator-active:has([data-lisonex-auth]) {
          background: #ffffff;
          color: #000000;
          height: 100dvh;
          overflow: hidden;
        }

        html:has(body.ea-configurator-active [data-lisonex-auth]) {
          height: 100dvh;
          overflow: hidden;
        }

        [data-lisonex-auth] {
          --auth-shell-y: clamp(14px, 3vh, 32px);
          --auth-panel-top: clamp(10px, 5.5vh, 72px);
          --auth-panel-bottom: clamp(22px, 4vh, 44px);
          --auth-tabs-mb: clamp(14px, 4vh, 56px);
          --auth-intro-mb: clamp(16px, 4vh, 48px);
          --auth-title-size: clamp(34px, 6vh, 52px);
          --auth-copy-size: clamp(15px, 2.1vh, 18px);
          --auth-field-h: clamp(52px, 8vh, 74px);
          --auth-action-mt: clamp(20px, 5vh, 40px);
          --auth-forgot-mt: clamp(28px, 10vh, 96px);
          height: 100dvh;
          max-height: 100dvh;
          overflow: hidden;
        }

        [data-lisonex-auth],
        [data-lisonex-auth] * {
          font-family:
            Arial,
            'Helvetica Neue',
            system-ui,
            -apple-system,
            BlinkMacSystemFont,
            'Segoe UI',
            sans-serif !important;
        }

        [data-lisonex-auth] input:not([type='checkbox']) {
          background: transparent !important;
          color: #000000 !important;
        }

        [data-lisonex-auth] select {
          background: transparent !important;
          color: #000000 !important;
        }

        [data-lisonex-auth] select:disabled {
          color: #777777 !important;
          opacity: 1;
        }

        [data-lisonex-auth] input::placeholder {
          color: #777777 !important;
          opacity: 1;
        }

        [data-radix-toast-viewport] {
          z-index: 99999 !important;
        }

        [data-lisonex-auth] .auth-scroll {
          height: 100%;
          min-height: 0;
          overflow-y: auto;
          overscroll-behavior: contain;
          scrollbar-width: thin;
          scrollbar-color: rgba(232, 117, 0, 0.75) transparent;
        }

        [data-lisonex-auth] .auth-scroll::-webkit-scrollbar {
          width: 6px;
        }

        [data-lisonex-auth] .auth-scroll::-webkit-scrollbar-track {
          background: transparent;
        }

        [data-lisonex-auth] .auth-scroll::-webkit-scrollbar-thumb {
          background: rgba(232, 117, 0, 0.75);
          border-radius: 999px;
        }

        [data-lisonex-auth] .auth-panel {
          padding-top: var(--auth-panel-top) !important;
          padding-bottom: var(--auth-panel-bottom) !important;
        }

        [data-lisonex-auth] .auth-tabs {
          height: clamp(46px, 6.8vh, 52px) !important;
        }

        [data-lisonex-auth] .auth-tabs-wrap {
          margin-bottom: var(--auth-tabs-mb) !important;
        }

        [data-lisonex-auth] .auth-intro {
          margin-bottom: var(--auth-intro-mb) !important;
        }

        [data-lisonex-auth] .auth-intro h1 {
          font-size: var(--auth-title-size) !important;
        }

        [data-lisonex-auth] .auth-intro p {
          margin-top: clamp(10px, 2.4vh, 24px) !important;
          font-size: var(--auth-copy-size) !important;
        }

        [data-lisonex-auth] .auth-field,
        [data-lisonex-auth] .auth-phone-field {
          height: var(--auth-field-h) !important;
        }

        [data-lisonex-auth] .lisonex-phone-input {
          position: relative;
          border: 1px solid #dedede !important;
          border-radius: 0 !important;
          background: #ffffff !important;
          outline: none !important;
          overflow: hidden;
        }

        [data-lisonex-auth] .lisonex-phone-input:focus-within {
          border-color: #e87500 !important;
          outline: 1px solid #e87500 !important;
          outline-offset: -1px;
        }

        [data-lisonex-auth] .lisonex-phone-input .react-international-phone-input-container {
          height: 100% !important;
          width: 100% !important;
          display: flex !important;
          align-items: stretch !important;
          background: #ffffff !important;
        }

        [data-lisonex-auth] .lisonex-phone-input .react-international-phone-country-selector {
          height: 100% !important;
          background: #fff3ea !important;
        }

        [data-lisonex-auth]
          .lisonex-phone-input
          .react-international-phone-country-selector-button {
          height: 100% !important;
          width: 68px !important;
          border: 0 !important;
          border-radius: 0 !important;
          background: #fff3ea !important;
          padding: 0 !important;
          color: #000000 !important;
        }

        [data-lisonex-auth] .lisonex-phone-input .react-international-phone-country-selector-button,
        [data-lisonex-auth]
          .lisonex-phone-input
          .react-international-phone-country-selector-button
          * {
          background-color: #fff3ea !important;
        }

        [data-lisonex-auth]
          .lisonex-phone-input
          .react-international-phone-country-selector::after {
          display: none !important;
        }

        [data-lisonex-auth]
          .lisonex-phone-input
          .react-international-phone-country-selector-button:hover {
          background: #fff3ea !important;
        }

        [data-lisonex-auth]
          .lisonex-phone-input
          .react-international-phone-country-selector-button__flag-emoji {
          margin: 0 6px 0 0 !important;
          font-size: 16px !important;
        }

        [data-lisonex-auth]
          .lisonex-phone-input
          .react-international-phone-country-selector-button__dropdown-arrow {
          border-top-color: #e87500 !important;
          margin-right: 0 !important;
        }

        [data-lisonex-auth] .lisonex-phone-input .react-international-phone-dial-code-preview {
          height: 100% !important;
          min-width: 58px !important;
          border: 0 !important;
          background: #fff3ea !important;
          color: #000000 !important;
          font-size: 19px !important;
          font-weight: 600 !important;
          padding: 0 12px 0 0 !important;
          display: flex !important;
          align-items: center !important;
          justify-content: flex-start !important;
        }

        [data-lisonex-auth] .lisonex-phone-input .react-international-phone-input {
          height: 100% !important;
          min-width: 0 !important;
          width: 100% !important;
          border: 0 !important;
          border-radius: 0 !important;
          background: #ffffff !important;
          color: #000000 !important;
          font-size: 20px !important;
          font-weight: 500 !important;
          padding: 0 24px !important;
          box-shadow: none !important;
        }

        [data-lisonex-auth] .lisonex-phone-input .react-international-phone-input::placeholder {
          color: #777777 !important;
          opacity: 1 !important;
        }

        [data-lisonex-auth]
          .lisonex-phone-input
          .react-international-phone-country-selector-dropdown {
          z-index: 30 !important;
          top: calc(100% + 6px) !important;
          width: min(360px, calc(100vw - 48px)) !important;
          max-height: 260px !important;
          border: 1px solid #dedede !important;
          background: #ffffff !important;
          color: #000000 !important;
          box-shadow: 0 20px 45px rgba(0, 0, 0, 0.16) !important;
        }

        [data-lisonex-auth]
          .lisonex-phone-input
          .react-international-phone-country-selector-dropdown__list-item {
          min-height: 38px !important;
          color: #000000 !important;
        }

        [data-lisonex-auth]
          .lisonex-phone-input
          .react-international-phone-country-selector-dropdown__list-item:hover,
        [data-lisonex-auth]
          .lisonex-phone-input
          .react-international-phone-country-selector-dropdown__list-item--focused {
          background: #fff3ea !important;
        }

        [data-lisonex-auth]
          .lisonex-phone-input
          .react-international-phone-country-selector-dropdown__list-item--selected {
          background: #061b29 !important;
          color: #ffffff !important;
        }

        [data-lisonex-auth] .auth-login-submit {
          margin-top: var(--auth-action-mt) !important;
        }

        [data-lisonex-auth] .auth-forgot {
          margin-top: var(--auth-forgot-mt) !important;
        }

        @media (min-width: 1024px) and (max-height: 820px) {
          [data-lisonex-auth] .auth-panel {
            padding-top: 1rem !important;
            padding-bottom: 1.25rem !important;
          }

          [data-lisonex-auth] .auth-tabs-wrap {
            margin-bottom: 1rem !important;
          }

          [data-lisonex-auth] .auth-intro {
            margin-bottom: 1rem !important;
          }

          [data-lisonex-auth] .auth-intro h1 {
            font-size: 38px !important;
          }

          [data-lisonex-auth] .auth-intro p {
            margin-top: 0.75rem !important;
            font-size: 16px !important;
          }

          [data-lisonex-auth] .auth-field,
          [data-lisonex-auth] .auth-phone-field {
            height: 56px !important;
          }

          [data-lisonex-auth] .auth-login-submit {
            margin-top: 1.5rem !important;
          }

          [data-lisonex-auth] .auth-forgot {
            margin-top: 2.5rem !important;
          }
        }

        @media (min-width: 1024px) and (max-height: 720px) {
          [data-lisonex-auth] {
            --auth-shell-y: 10px;
          }

          [data-lisonex-auth] .auth-panel {
            padding-top: 0.25rem !important;
            padding-bottom: 1rem !important;
          }

          [data-lisonex-auth] .auth-tabs-wrap {
            margin-bottom: 0.75rem !important;
          }

          [data-lisonex-auth] .auth-intro {
            margin-bottom: 0.75rem !important;
          }

          [data-lisonex-auth] .auth-intro h1 {
            font-size: 34px !important;
          }

          [data-lisonex-auth] .auth-intro p {
            margin-top: 0.75rem !important;
            font-size: 16px !important;
          }

          [data-lisonex-auth] .auth-field,
          [data-lisonex-auth] .auth-phone-field {
            height: 52px !important;
          }
        }

        @media (max-width: 767px) {
          [data-lisonex-auth] {
            --auth-shell-y: 18px;
            --auth-panel-top: clamp(26px, 7vh, 56px);
            --auth-panel-bottom: 24px;
            --auth-tabs-mb: 28px;
            --auth-intro-mb: 28px;
            --auth-title-size: clamp(38px, 10vw, 48px);
            --auth-copy-size: 18px;
            --auth-field-h: 68px;
            --auth-action-mt: 34px;
            --auth-forgot-mt: 56px;
          }

          [data-lisonex-auth] .auth-scroll {
            padding-left: 24px !important;
            padding-right: 24px !important;
            overflow-x: hidden !important;
          }

          [data-lisonex-auth] .auth-panel {
            max-width: calc(100vw - 48px) !important;
            width: calc(100vw - 48px) !important;
            min-width: 0 !important;
          }

          [data-lisonex-auth] .auth-tabs-wrap,
          [data-lisonex-auth] .auth-tabs,
          [data-lisonex-auth] .auth-field,
          [data-lisonex-auth] .auth-phone-field,
          [data-lisonex-auth] form,
          [data-lisonex-auth] form > div {
            max-width: 100% !important;
          }

          [data-lisonex-auth] .auth-tabs-wrap,
          [data-lisonex-auth] .auth-tabs,
          [data-lisonex-auth] .auth-field,
          [data-lisonex-auth] .auth-phone-field {
            max-width: calc(100vw - 48px) !important;
            width: calc(100vw - 48px) !important;
          }

          [data-lisonex-auth] .auth-tabs {
            width: 100% !important;
            min-width: 0 !important;
          }

          [data-lisonex-auth] .auth-tabs button {
            min-width: 0 !important;
            padding-left: 8px !important;
            padding-right: 8px !important;
            font-size: 15px !important;
          }

          [data-lisonex-auth] .auth-intro,
          [data-lisonex-auth] .auth-intro p {
            max-width: 100% !important;
            overflow-wrap: anywhere !important;
          }

          [data-lisonex-auth] .auth-field,
          [data-lisonex-auth] .auth-phone-field {
            width: 100% !important;
            min-width: 0 !important;
          }

          [data-lisonex-auth] form .grid {
            grid-template-columns: minmax(0, 1fr) !important;
            min-width: 0 !important;
          }

          [data-lisonex-auth] form .auth-tabs {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }

          [data-lisonex-auth] .auth-field input,
          [data-lisonex-auth] .auth-phone-field input {
            min-width: 0 !important;
          }

          [data-lisonex-auth] .lisonex-phone-input .react-international-phone-input {
            padding-left: 18px !important;
            padding-right: 18px !important;
            font-size: 18px !important;
          }

          [data-lisonex-auth]
            .lisonex-phone-input
            .react-international-phone-country-selector-button {
            width: 56px !important;
          }

          [data-lisonex-auth] .lisonex-phone-input .react-international-phone-dial-code-preview {
            min-width: 50px !important;
            padding-right: 8px !important;
            font-size: 17px !important;
          }
        }

        @media (max-width: 420px) {
          [data-lisonex-auth] .auth-scroll {
            padding-left: 20px !important;
            padding-right: 20px !important;
          }

          [data-lisonex-auth] .auth-panel,
          [data-lisonex-auth] .auth-tabs-wrap,
          [data-lisonex-auth] .auth-tabs,
          [data-lisonex-auth] .auth-field,
          [data-lisonex-auth] .auth-phone-field {
            max-width: calc(100vw - 40px) !important;
            width: calc(100vw - 40px) !important;
          }

          [data-lisonex-auth] .auth-intro p {
            max-width: 100% !important;
            margin-left: auto !important;
            margin-right: auto !important;
          }
        }
      `}</style>
      <Toaster />
      <div className="grid h-full min-h-0 lg:grid-cols-[minmax(0,57.6%)_minmax(0,42.4%)]">
        <section className="relative h-full min-h-0 overflow-hidden bg-white">
          <div className="auth-scroll flex h-full min-h-0 flex-col px-6 sm:px-10 lg:px-16">
            <div className="flex shrink-0 items-start gap-6 pt-[var(--auth-shell-y)]">
              <LisonexWordmark className="text-[46px] sm:text-[50px]" />
            </div>

            <div className="auth-panel mx-auto flex w-full max-w-[494px] flex-col justify-start px-0 pb-0 pt-10 sm:pt-14 lg:pt-20">
              {children}
            </div>
          </div>
        </section>

        <aside className="relative hidden h-full min-h-0 overflow-hidden bg-[#0b2434] lg:block">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${authBackgroundUrl})` }}
          />
          <div className="absolute inset-0 bg-[#0a2537]/10 mix-blend-multiply" />
          <div className="absolute left-[-6px] top-1/2 h-[400px] w-3 -translate-y-1/2 bg-[#e87500]" />
        </aside>
      </div>
    </main>
  );
}

function SectionIntro({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="auth-intro mb-12 text-center">
      <h1 className="text-[48px] font-semibold leading-tight tracking-[-0.04em] text-black sm:text-[52px]">
        {title}
      </h1>
      <p className="mx-auto mt-6 max-w-[420px] text-[18px] leading-snug text-[#707070]">
        {subtitle}
      </p>
    </div>
  );
}

function StepActions({
  step,
  isSubmitting,
  canContinue,
  onBack,
}: {
  step: RegisterStep;
  isSubmitting: boolean;
  canContinue: boolean;
  onBack: () => void;
}) {
  return (
    <div className="mt-8 flex items-center justify-between gap-3">
      <span className="text-[20px] text-[#777]">Krok {step}/3</span>
      <div className="flex items-center gap-3">
        {step > 1 && (
          <SecondaryButton onClick={onBack} className="px-6">
            <ArrowLeft className="h-4 w-4" strokeWidth={2.4} />
            Späť
          </SecondaryButton>
        )}
        <PrimaryButton type="submit" disabled={!canContinue || isSubmitting}>
          {isSubmitting ? 'Spracúvam...' : step === 3 ? 'Vytvoriť účet' : 'Pokračovať'}
        </PrimaryButton>
      </div>
    </div>
  );
}

function getValidationMessage(error: unknown, fallback: string) {
  if (error instanceof ValidationError) {
    return Object.values(error.fields).flat().at(0) ?? fallback;
  }

  return (error as Error)?.message || fallback;
}

const PASSWORD_REQUIREMENTS_MESSAGE =
  'Heslo mus\u00ed obsahova\u0165 minim\u00e1lne 6 a\u017e 64 znakov, jedno ve\u013ek\u00e9 p\u00edsmeno a jedno \u010d\u00edslo.';

function getPasswordValidationMessage(password: string) {
  const hasUppercaseLetter = password !== password.toLocaleLowerCase();
  const hasNumber = /\d/.test(password);

  if (password.length < 6 || password.length > 64 || !hasUppercaseLetter || !hasNumber) {
    return PASSWORD_REQUIREMENTS_MESSAGE;
  }

  return null;
}

type ConfiguratorAuthPageProps = {
  locale: string;
};

export function ConfiguratorAuthPage({ locale }: ConfiguratorAuthPageProps) {
  const { t } = useTranslations();
  const router = useRouter();
  const { toast } = useToast();
  const [mode, setMode] = useState<AuthMode>('login');
  const [registerStep, setRegisterStep] = useState<RegisterStep>(1);
  const [state, setState] = useState<AuthState>(initialState);
  const [pendingAuth, setPendingAuth] = useState<PendingAuth | null>(null);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countries, setCountries] = useState<Country[]>([]);
  const [isCountriesLoading, setIsCountriesLoading] = useState(false);

  useEffect(() => {
    document.body.classList.add('ea-configurator-active');

    return () => {
      document.body.classList.remove('ea-configurator-active');
    };
  }, []);

  const updateState = (patch: Partial<AuthState>) => {
    setState(previous => {
      const next = { ...previous, ...patch };

      for (const key of AUTH_STRING_KEYS) {
        if (next[key] == null) {
          (next as Record<string, unknown>)[key] = '';
        }
      }

      return next;
    });
  };

  const showAuthError = useCallback(
    (message: string) => {
      toast({
        title: message,
        variant: 'destructive',
      });
    },
    [toast],
  );

  const setError = (message: string | null) => {
    if (!message) return;
    const readableMessage = message
      .replace('Hesl\u0102\u02c7 sa nezhoduj\u0102\u015f.', 'Hesl\u00e1 sa nezhoduj\u00fa.')
      .replace(
        'Skontrolujte zadan\u0102\u00a9 \u0102\u015fdaje.',
        'Skontrolujte zadan\u00e9 \u00fadaje.',
      );
    showAuthError(readableMessage);
  };

  useEffect(() => {
    let isMounted = true;

    setIsCountriesLoading(true);
    fetchCountries({ locale })
      .then(response => {
        if (!isMounted) return;

        setCountries(response.data);
        setState(previous => {
          if (previous.country) return previous;
          return { ...previous, country: resolveDefaultCountryId(response.data) };
        });
      })
      .catch(() => {
        if (!isMounted) return;
        setCountries([]);
        showAuthError('Nepodarilo sa načítať krajiny.');
      })
      .finally(() => {
        if (isMounted) {
          setIsCountriesLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [locale, showAuthError]);

  const handleAuthSuccess = async (authToken: string, customer: any) => {
    await setAuthToken({ authToken });
    initCustomerStore({ authToken, customer });
    router.refresh();
  };

  const canContinue = useMemo(() => {
    if (registrationComplete) return true;

    if (mode === 'login') {
      return !!state.email && !!state.password;
    }

    if (registerStep === 1) {
      return !!state.email && !!state.password && !!state.passwordConfirmation;
    }

    if (registerStep === 2) {
      const hasCustomerData =
        state.customerKind === 'company'
          ? !!state.company && !!state.ico
          : !!state.name && !!state.surname;

      return (
        hasCustomerData &&
        !!state.street &&
        !!state.streetNumber &&
        !!state.postcode &&
        !!state.city &&
        !!state.country
      );
    }

    if (state.customerKind === 'company') {
      return (
        !!state.contactName &&
        !!state.contactEmail &&
        !!normalizeSlovakPhone(state.phone) &&
        state.gdpr
      );
    }

    return !!normalizeSlovakPhone(state.phone) && state.gdpr;
  }, [mode, registerStep, registrationComplete, state]);

  const switchMode = (nextMode: AuthMode) => {
    setMode(nextMode);
    setRegisterStep(1);
    setRegistrationComplete(false);
    setPendingAuth(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canContinue || isSubmitting) return;

    if (mode === 'register' && registerStep < 3) {
      const passwordValidationMessage = getPasswordValidationMessage(state.password);
      if (registerStep === 1 && passwordValidationMessage) {
        setError(passwordValidationMessage);
        return;
      }

      if (registerStep === 1 && state.password !== state.passwordConfirmation) {
        setError('Heslá sa nezhodujú.');
        return;
      }

      setRegisterStep(previous => (previous === 1 ? 2 : 3));
      setError(null);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (mode === 'login') {
        const response = await signIn({
          email: state.email,
          password: state.password,
          'g-recaptcha-response': '',
          locale,
        });

        await handleAuthSuccess(response.data.token, response.data.customer);
        return;
      }

      const passwordValidationMessage = getPasswordValidationMessage(state.password);
      if (passwordValidationMessage) {
        setError(passwordValidationMessage);
        return;
      }

      if (state.password !== state.passwordConfirmation) {
        setError('Heslá sa nezhodujú.');
        return;
      }

      const normalizedPhone = normalizeSlovakPhone(state.phone);
      const isCompany = state.customerKind === 'company';
      const contactName = isCompany ? state.contactName : `${state.name} ${state.surname}`.trim();
      const registerName = isCompany ? contactName.split(/\s+/)[0] || state.company : state.name;
      const registerSurname = isCompany
        ? contactName.split(/\s+/).slice(1).join(' ') || state.company
        : state.surname;
      const country = state.country ? { id: state.country } : undefined;
      const companyData = isCompany
        ? {
            company: state.company,
            ico: state.ico,
            dic: state.dic,
            ic_dph: state.icDph,
            company_contact_name: state.contactName,
            company_mail: state.contactEmail,
            company_phone: normalizedPhone,
          }
        : {};

      const response = await register({
        name: registerName,
        surname: registerSurname,
        email: state.email,
        username: state.email,
        phone: normalizedPhone,
        address: state.street,
        street_number: state.streetNumber,
        postcode: state.postcode,
        city: state.city,
        country,
        company_buy: isCompany,
        ...companyData,
        password: state.password,
        password_confirmation: state.passwordConfirmation,
        accepted_marketing: false,
        gdpr: state.gdpr,
        tos: state.gdpr,
        newsletter: false,
        newsletter_return_url: `${window.location.origin}/${locale}/newsletter-confirm`,
        'g-recaptcha-response': '',
        locale,
      } as any);

      setPendingAuth({
        token: response.data.token,
        customer: response.data.customer,
      });
      setRegistrationComplete(true);
    } catch (authError) {
      setError(getValidationMessage(authError, 'Skontrolujte zadané údaje.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (registrationComplete) {
    return (
      <AuthShell>
        <div className="flex flex-col items-center text-center">
          <div className="mb-10 flex h-[62px] w-[62px] items-center justify-center rounded-[18px] bg-[#008900] text-white">
            <Check className="h-9 w-9" strokeWidth={3} />
          </div>
          <h1 className="max-w-[520px] text-[48px] font-semibold leading-[1.08] tracking-[-0.04em] text-black sm:text-[52px]">
            Váš účet bol úspešne vytvorený
          </h1>
          <p className="mx-auto mt-8 max-w-[520px] text-[18px] leading-snug text-black">
            Na vašu e-mailovú adresu pri registrácii sme zaslali aktivačný link. Aktivujte váš účet
            a začnite s konfiguráciou.
          </p>
          <div className="mt-12 flex flex-wrap justify-center gap-3">
            <PrimaryButton
              onClick={() => {
                if (pendingAuth) {
                  void handleAuthSuccess(pendingAuth.token, pendingAuth.customer);
                }
              }}
              disabled={!pendingAuth}
            >
              <SlidersHorizontal className="h-5 w-5" strokeWidth={2.2} />
              Začať s konfiguráciou
            </PrimaryButton>
            <SecondaryButton onClick={() => router.push(`/${locale}/konfigurator`)}>
              Domovská stránka
            </SecondaryButton>
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <form onSubmit={handleSubmit} className="w-full">
        <div className="auth-tabs-wrap mb-14">
          <ModeTabs mode={mode} onChange={switchMode} />
        </div>

        {mode === 'login' ? (
          <>
            <SectionIntro
              title="Prihlásenie"
              subtitle="Prihláste sa do svojho účtu a pokračujte tam, kde ste skončili."
            />

            <div className="grid gap-6">
              <TextInput
                icon={Mail}
                placeholder="E-mail"
                type="email"
                value={state.email}
                autoComplete="email"
                onChange={value => updateState({ email: value })}
              />
              <TextInput
                icon={Lock}
                rightIcon={Eye}
                placeholder="Heslo"
                type="password"
                value={state.password}
                autoComplete="current-password"
                onChange={value => updateState({ password: value })}
              />
            </div>

            <div className="auth-login-submit mt-10 flex justify-center">
              <PrimaryButton type="submit" disabled={!canContinue || isSubmitting}>
                {isSubmitting ? 'Spracúvam...' : 'Prihlásiť sa'}
              </PrimaryButton>
            </div>

            <div className="auth-forgot mt-24 text-center">
              <button
                type="button"
                className="text-[18px] font-semibold text-[#d86600] underline underline-offset-2 transition hover:text-[#a94800]"
              >
                Zabudli ste heslo?
              </button>
            </div>
          </>
        ) : (
          <>
            <SectionIntro
              title="Registrácia"
              subtitle="Vytvorte svoj účet a začnite s konfiguráciou."
            />

            {registerStep === 1 && (
              <div className="grid gap-6">
                <h2 className="text-[24px] font-semibold tracking-[-0.03em] text-black">
                  Prihlasovacie údaje
                </h2>
                <TextInput
                  icon={Mail}
                  placeholder="E-mail *"
                  type="email"
                  value={state.email}
                  autoComplete="email"
                  onChange={value => updateState({ email: value })}
                />
                <TextInput
                  icon={Lock}
                  rightIcon={Eye}
                  placeholder="Heslo *"
                  type="password"
                  value={state.password}
                  autoComplete="new-password"
                  onChange={value => updateState({ password: value })}
                />
                <TextInput
                  icon={Lock}
                  rightIcon={Eye}
                  placeholder="Heslo znovu *"
                  type="password"
                  value={state.passwordConfirmation}
                  autoComplete="new-password"
                  onChange={value => updateState({ passwordConfirmation: value })}
                />
                <p className="text-[12px] leading-snug text-[#777]">
                  {PASSWORD_REQUIREMENTS_MESSAGE}
                </p>
              </div>
            )}

            {registerStep === 2 && (
              <div className="grid gap-6">
                <h2 className="text-[24px] font-semibold tracking-[-0.03em] text-black">
                  Vaše údaje
                </h2>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => updateState({ customerKind: 'person' })}
                    className={cn(
                      'flex h-24 flex-col items-center justify-center gap-2 border text-[16px] font-semibold transition',
                      state.customerKind === 'person'
                        ? 'border-[#061b29] bg-[#061b29] text-white'
                        : 'border-[#dedede] bg-white text-black hover:border-[#e87500]',
                    )}
                  >
                    <IconBox icon={User} active={state.customerKind === 'person'} />
                    Fyzická osoba
                  </button>
                  <button
                    type="button"
                    onClick={() => updateState({ customerKind: 'company' })}
                    className={cn(
                      'flex h-24 flex-col items-center justify-center gap-2 border text-[16px] font-semibold transition',
                      state.customerKind === 'company'
                        ? 'border-[#061b29] bg-[#061b29] text-white'
                        : 'border-[#dedede] bg-white text-black hover:border-[#e87500]',
                    )}
                  >
                    <IconBox icon={Building2} active={state.customerKind === 'company'} />
                    Spoločnosť
                  </button>
                </div>
                {state.customerKind === 'person' ? (
                  <>
                    <TextInput
                      placeholder="Meno *"
                      value={state.name}
                      autoComplete="given-name"
                      onChange={value => updateState({ name: value })}
                    />
                    <TextInput
                      placeholder="Priezvisko *"
                      value={state.surname}
                      autoComplete="family-name"
                      onChange={value => updateState({ surname: value })}
                    />
                  </>
                ) : (
                  <>
                    <TextInput
                      placeholder="Názov firmy *"
                      value={state.company}
                      autoComplete="organization"
                      onChange={value => updateState({ company: value })}
                    />
                    <div className="grid gap-3 sm:grid-cols-3">
                      <TextInput
                        placeholder="IČO *"
                        value={state.ico}
                        autoComplete="off"
                        onChange={value => updateState({ ico: value })}
                      />
                      <TextInput
                        placeholder="DIČ"
                        value={state.dic}
                        autoComplete="off"
                        onChange={value => updateState({ dic: value })}
                      />
                      <TextInput
                        placeholder="IČ DPH"
                        value={state.icDph}
                        autoComplete="off"
                        onChange={value => updateState({ icDph: value })}
                      />
                    </div>
                  </>
                )}
                <div className="grid grid-cols-[minmax(0,1fr)_130px] gap-3">
                  <TextInput
                    placeholder="Ulica *"
                    value={state.street}
                    autoComplete="address-line1"
                    onChange={value => updateState({ street: value })}
                  />
                  <TextInput
                    placeholder="Číslo *"
                    value={state.streetNumber}
                    autoComplete="address-line2"
                    onChange={value => updateState({ streetNumber: value })}
                  />
                </div>
                <div className="grid grid-cols-[130px_minmax(0,1fr)] gap-3">
                  <TextInput
                    placeholder="PSČ *"
                    value={state.postcode}
                    autoComplete="postal-code"
                    onChange={value => updateState({ postcode: value })}
                  />
                  <TextInput
                    placeholder="Mesto *"
                    value={state.city}
                    autoComplete="address-level2"
                    onChange={value => updateState({ city: value })}
                  />
                </div>
                <label className="block">
                  <span className="sr-only">Vyberte krajinu</span>
                  <div className="flex h-[74px] items-center border border-[#dedede] bg-white px-6">
                    <select
                      value={state.country ?? ''}
                      disabled={isCountriesLoading || countries.length === 0}
                      onChange={event => updateState({ country: event.target.value })}
                      className="h-full min-w-0 flex-1 appearance-none bg-transparent text-[20px] font-medium text-black outline-none disabled:text-[#777]"
                    >
                      <option value="">
                        {isCountriesLoading ? 'Načítavam krajiny...' : 'Vyberte krajinu'}
                      </option>
                      {countries.map(country => (
                        <option key={country.id} value={country.id}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="h-5 w-5 text-[#e87500]" strokeWidth={2.2} />
                  </div>
                </label>
              </div>
            )}

            {registerStep === 3 && (
              <div className="grid gap-6">
                <h2 className="text-[24px] font-semibold tracking-[-0.03em] text-black">
                  {state.customerKind === 'company' ? 'Kontaktná osoba' : 'Dokončenie registrácie'}
                </h2>
                {state.customerKind === 'company' && (
                  <>
                    <TextInput
                      placeholder="Meno kontaktnej osoby *"
                      value={state.contactName}
                      autoComplete="name"
                      onChange={value => updateState({ contactName: value })}
                    />
                    <TextInput
                      placeholder={t('text4091')}
                      type="email"
                      value={state.contactEmail}
                      autoComplete="email"
                      onChange={value => updateState({ contactEmail: value })}
                    />
                  </>
                )}
                <PhoneInputField
                  value={state.phone ?? ''}
                  onChange={phone => updateState({ phone: phone ?? '' })}
                  placeholder="Telefón 09..."
                  inputProps={{ autoComplete: 'tel' }}
                  inputClassName="lisonex-phone-native-input"
                  inputStyle={{
                    height: '100%',
                    minWidth: 0,
                    width: '100%',
                    border: 'none',
                    borderRadius: 0,
                    backgroundColor: '#ffffff',
                    color: '#000000',
                    fontSize: '20px',
                    fontWeight: 500,
                    padding: '0 24px',
                    boxShadow: 'none',
                  }}
                  countrySelectorStyleProps={{
                    buttonClassName: 'lisonex-phone-country-button',
                    buttonStyle: {
                      height: '100%',
                      width: '68px',
                      border: 'none',
                      borderRadius: 0,
                      backgroundColor: '#fff3ea',
                      padding: 0,
                      color: '#000000',
                    },
                  }}
                  disableDialCodeAndPrefix
                  showDisabledDialCodeAndPrefix
                  className="auth-phone-field lisonex-phone-input h-[74px]"
                />
                <div className="hidden">
                  <button
                    type="button"
                    className="flex w-[126px] items-center justify-center gap-3 bg-[#fff3ea] text-[19px] font-semibold text-black"
                  >
                    <span className="text-base">🇸🇰</span>
                    +421
                    <ChevronDown className="h-4 w-4 text-[#e87500]" strokeWidth={2.4} />
                  </button>
                  <label className="flex min-w-0 flex-1 items-center">
                    <span className="sr-only">Telefón</span>
                    <Phone className="ml-5 h-5 w-5 text-[#777]" strokeWidth={1.7} />
                    <input
                      value={state.phone ?? ''}
                      onChange={event => updateState({ phone: event.target.value })}
                      placeholder="Telefón 09..."
                      autoComplete="tel"
                      className="h-full min-w-0 flex-1 bg-transparent px-4 text-[20px] font-medium text-black outline-none placeholder:text-[#777]"
                    />
                  </label>
                </div>
                <AuthCheckbox
                  checked={state.gdpr}
                  onChange={checked => updateState({ gdpr: checked })}
                >
                  Súhlasím so{' '}
                  <span className="text-[#d86600] underline underline-offset-2">
                    spracovaním osobných údajov
                  </span>{' '}
                  pre účely vytvorenia účtu.
                </AuthCheckbox>
              </div>
            )}

            <StepActions
              step={registerStep}
              isSubmitting={isSubmitting}
              canContinue={canContinue}
              onBack={() => {
                setRegisterStep(previous => (previous === 3 ? 2 : 1));
                setError(null);
              }}
            />
          </>
        )}
      </form>
    </AuthShell>
  );
}
